import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import api from '../../../service/api'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import noLogsImg from '../../../assets/no-logs.svg'
import { Avatar, Box, Button, Collapse, Divider, Popover, Typography, useTheme } from '@mui/material'
import { Icons } from '../../../utils/utilities'
import { useNavigate } from 'react-router-dom'
import CustomChip from '../../common/CustomChip'
import dayjs from 'dayjs'


const getNameInitials = ( name ) => {
    try {
        const nameParts = name?.split( " " )
        return nameParts.map( n => n?.at( 0 ) ).slice( 0, 2 )
    } catch ( err ) {
        return name?.at( 0 )
    }
}

const IssueBlock = ( { issue, handlePopoverOpen, handlePopoverClose } ) => {

    const [openDetails, setOpenDetails] = useState( false )

    const { border, palette, layout } = useTheme()

    const topics = Object.values( issue.practice_session_details.modules_and_topics ).map( item => item.topics )

    return (
        <Box border={border[1]} borderRadius={layout.borderRadius} padding="10px" key={issue._id}>
            <Box display="flex" gap="5px" flexWrap="wrap" alignItems="center">
                <CustomChip variant='filled' startIcon={Icons.default.Calendar} value={dayjs( issue.createdAt ).format( "LLL" )} />
            </Box>

            <Box>
                <Box marginTop="5px" display="flex" gap="5px" alignItems="center">
                    <Avatar sx={{ background: palette.primary.main, color: palette.primary.contrastText, width: "30px", height: "30px", textTransform: "uppercase", fontSize: "16px" }} >{getNameInitials( issue.user_info?.user_name )}</Avatar>
                    <Typography>
                        <Typography variant='caption' component="em">Reported by </Typography>
                        <Typography
                            onMouseEnter={event => handlePopoverOpen( event, issue.user_info )}
                            onMouseLeave={handlePopoverClose}
                            variant='subtitle2'
                            fontSize="12px"
                            fontWeight="normal"
                            position="relative"
                            sx={{
                                cursor: "pointer",
                                "&:after": {
                                    content: "''",
                                    position: "absolute",
                                    display: "block",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    width: "100%",
                                    borderBottom: `1px dashed ${palette.text.secondary}`,
                                    zIndex: 10
                                }
                            }}
                        >
                            {issue.user_info.name}
                        </Typography>
                    </Typography>
                </Box>
                <Box marginTop="5px" display="flex" gap="5px" flexWrap="wrap" alignItems="center">
                    <CustomChip textStyles={{ textTransform: "uppercase" }} value={issue.issue_details.value} />
                </Box>
            </Box>

            <Box display="flex" gap={layout.sectionsGap} alignItems="flex-end" justifyContent="space-between">
                <Box>
                    <Typography component="p" variant='subtitle2' color='textPrimary'>
                        {issue.issue_details.label}
                    </Typography>
                    <Typography component="p" variant='caption' color='textSecondary'>
                        {issue.issue_details.description}
                    </Typography>
                </Box>
                <Button onClick={() => setOpenDetails( prev => !prev )} color='greyed' variant='outlined' sx={{ borderRadius: "20px", flexShrink: 0 }} size='small' endIcon={openDetails ? Icons.default.KeyboardArrowUp : Icons.default.KeyboardArrowDownIcon} >
                    {openDetails ? "Close Details" : "Open Details"}
                </Button>
            </Box>

            <Collapse in={openDetails}  >
                <Box display="flex" flexDirection="column" gap={layout.cardsGap} marginTop="10px" padding="10px 0" borderTop={border[1]}>
                    <Box>
                        <Typography variant='subtitle2' component="h6">Practice session</Typography>
                        <Typography fontSize="12px" component="span">{issue?.practice_session_details?.title}</Typography>
                    </Box>
                    <Box>
                        <Typography variant='subtitle2' component="h6">Created By</Typography>
                        <Typography fontSize="12px" component="span">{issue?.practice_session_details?.creator_info?.name}</Typography>
                    </Box>
                    <Box>
                        <Typography variant='subtitle2' component="h6">Topics</Typography>
                        <Typography fontSize="12px" component="span">{topics?.join( ", " )}</Typography>
                    </Box>

                    {issue.issue_details.question_details && <Box>
                        <Typography variant='subtitle2' component="h6">Question Details</Typography>
                        <Box >
                            <Typography maxHeight="200px" overflow="auto" className='slim-custom-scrollbar' fontSize="12px" padding="10px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} component="pre">{JSON.stringify( issue.issue_details.question_details, null, 3 )}</Typography>
                        </Box>
                    </Box>}
                </Box>
            </Collapse>
        </Box>
    )
}

const AuditPracticeSessionsIssues = () => {

    const [userInfoPopoveAnchorEl, setUserInfoPopoveAnchorEl] = useState( null )
    const [hoveredUserData, setHoveredUserData] = useState( null )
    const [issues, setIssues] = useState( [] )
    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )

    const { palette, layout } = useTheme()
    const navigate = useNavigate()

    const open = Boolean( userInfoPopoveAnchorEl ) && Boolean( hoveredUserData )

    const handlePopoverOpen = ( event, data ) => {
        setUserInfoPopoveAnchorEl( event.currentTarget )
        setHoveredUserData( data )
    }

    const handlePopoverClose = () => {
        setUserInfoPopoveAnchorEl( null )
        setHoveredUserData( null )
    }

    const navigateBack = () => {
        return navigate( `/admin/practice-with-simba` )
    }

    const getIssues = useCallback( async () => {
        try {
            const { data } = await api.simbaAI.fetchReportedIssues()
            setIssues( data.data )
            setError( null )
        } catch ( err ) {
            console.log( err )
            setError( { message: err?.response?.data?.message || "Something went wrong while fetching practice sessions reported issues.", code: err?.response?.status || 500 } )
        } finally {
            setLoading( false )
        }
    }, [] )

    useEffect( () => {
        getIssues()
    }, [getIssues] )

    return (
        <Box padding={layout.pagePadding} flexGrow={1} overflow="auto" display="flex" flexDirection="column" gap={layout.sectionsGap}>

            <Box alignItems="center" gap="20px" flexWrap="wrap" display="flex" justifyContent="space-between">
                <Box>
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Practice Sessions Issue reports</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>Check all the issues reports made by the users on practice session questions.</Typography>
                </Box>
                <Button onClick={navigateBack} startIcon={Icons.default.ArrowBack} variant='outlined' color='secondary'>
                    Back to practice sessions audit
                </Button>
            </Box>


            {!loading && issues.length > 0 && < Box display="flex" gap="10px" flexDirection="column" overflow="auto"  >
                {issues.map( issue => {
                    return (
                        <IssueBlock handlePopoverOpen={handlePopoverOpen} handlePopoverClose={handlePopoverClose} key={issue._id} issue={issue} />
                    )
                } )}

            </Box>}

            {/* User Details Popover */}
            <Popover
                id="mouse-over-popover"
                sx={{ pointerEvents: 'none' }}
                open={open}
                anchorEl={userInfoPopoveAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            boxShadow: "none"
                        }
                    },
                    transition: {
                        timeout: { exit: 0 }
                    }
                }}
                disableRestoreFocus
            >
                <Box width={300} bgcolor={palette.contentBg}>
                    <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="flex-start" padding="10px">
                        <Box>
                            <Typography variant='h6' fontWeight="800" fontSize="16px" component="h1">
                                {hoveredUserData?.name}
                            </Typography>
                            {hoveredUserData?.role !== "STUDENT" && <Typography fontSize="12px" color='textSecondary'>
                                {hoveredUserData?.empcode}
                            </Typography>}
                            {hoveredUserData?.role === "STUDENT" && <Typography display="flex" alignItems="center" gap="5px" fontSize="12px" color='textSecondary'>
                                <Typography component="span" fontSize="inherit" fontWeight="inherit">{hoveredUserData?.user_id}</Typography>
                                {hoveredUserData?.usn && <Typography display="flex" alignItems="center" component="span" fontSize="5px" fontWeight="inherit">{Icons.default.FiberManualRecordIcon}</Typography>}
                                {hoveredUserData?.usn && <Typography component="span" fontSize="inherit" fontWeight="inherit">{hoveredUserData?.usn}</Typography>}
                            </Typography>}
                        </Box>
                        <CustomChip variant='filled' color="primary" value={hoveredUserData?.role} />
                    </Box>
                    <Divider />
                    {hoveredUserData?.role === "STUDENT" && <Box padding="10px">
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Institute</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.institute || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Course</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.course_short_name || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Branch</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.course_branch_short_name || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Current Year</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.current_year || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Current Semester</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.current_sem || "-"}</Typography>
                        </Box>
                    </Box>}
                    {hoveredUserData?.role !== "STUDENT" && <Box padding="10px">
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Institute</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.institute || "-"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={layout.cardsGap} alignItems="center" >
                            <Typography color='textSecondary' fontSize="12px" variant='subtitle2'>Department</Typography>
                            <Typography fontSize="12px" textAlign="right" variant='subtitle2'>{hoveredUserData?.department_name || "-"}</Typography>
                        </Box>
                    </Box>}
                </Box>
            </Popover>

            {!loading && issues.length === 0 && < Box alignItems="center" display="flex" gap="10px" flexDirection="column" bgcolor={palette.contentBg} padding="40px 20px"  >
                <Box component="img" width="180px" src={noLogsImg} />
                <Typography variant="subtitle2">No reports present to show!</Typography>
            </Box>}

            {loading && !error && <DataLoadingSpinner padding='0' waitingMessage="Loading practice sessions issue report please wait..." />}

            {error && <TryAgainMessageBlock err={error.message} code={error.code} getData={getIssues} />}

        </Box>
    )
}

export default AuditPracticeSessionsIssues
